import { ALLMOST_DEFAULT_RUNTIME, allmostDefaultRuntimeModules, allmostDescriptors, allmostModules, allmostPatch, runAdditiveQaRoute, runCombQaRoute, runDelayReferenceAudio, runModulationReferenceAudio, runReverbReferenceAudio, runMixerQaRoute, runSpatializerQaRoute, runResamplerQaRoute, runSelfBufferQaRoute, runStringQaRoute, runBoreQaRoute, runFrictionQaRoute, runGranularQaRoute, runConvolutionQaRoute, runOfflineRendererQaRoute, runModuleLabAudio, runProcessingReferenceAudio, runReferenceChainAudio, type ModuleLabFilterMode } from './audio-engine.js';
import { allmostGraphModules, allmostGraphDescriptors } from './audio-engine.js';
import { createPatchBay } from './patch-bay.js';
// Fixed provider-completion metadata; never synthesize a timestamp on page load.
void fetch('/deployment-version.json', { cache: 'no-store' }).then(r => r.ok ? r.json() : null).then(data => {
  const label = document.querySelector('#deployment-version');
  if (label && /^v\d{3,} \d{8}_\d{6}$/.test(data?.version ?? '')) label.textContent = data.version;
}).catch(() => { /* Honest unverified placeholder remains. */ });
import { renderEditedGraph, graphAudioOutputs } from './graph-renderer.js';
import { createBrowserLiveGraph } from './live-graph-host.js';
import { liveGraphExample } from './live-graph-example.js';
import { hostDefinitions } from './live-graph-modules.js';
import { liveDefinitions, normalizedNode } from './live-graph-core.js';
import {
  LiveInstrumentController,
  WebAudioVoiceSink,
  type DualOscillatorSettings,
  type EnvelopeSettings,
  type OscillatorSettings,
} from './live-instrument.js';
import { PerformanceFocusPolicy } from './performance-focus.js';
import type {
  LfoPolarity,
  LfoRateMode,
  LfoWaveform,
  TransportDivision,
} from '../../packages/mod-015/src/index.js';
import type { LiveFilterReadback, LiveFilterSettings } from './live-modulated-filter.js';

const required = <T extends Element>(selector: string): T => {
  const found = document.querySelector<T>(selector);
  if (!found) throw new Error(`missing UI element: ${selector}`);
  return found;
};
const status = required<HTMLElement>('#status');
const graph = required<SVGSVGElement>('#graph');
const startButton = required<HTMLButtonElement>('#start');
const stopButton = required<HTMLButtonElement>('#stop');
const moduleCatalog = required<HTMLUListElement>('#module-catalog');
const patchBayStatus = required<HTMLOutputElement>('#patch-status');
const patchOverlay = required<HTMLDialogElement>('#patch-overlay');
const patchOpen = required<HTMLButtonElement>('#patch-open');
const patchClose = required<HTMLButtonElement>('#patch-close');
const patchWorkspace = required<HTMLElement>('#patch-workspace');
const patchContextMenu = required<HTMLElement>('#patch-context-menu');
const midiConnect = required<HTMLButtonElement>('#midi-connect');
const midiStatus = required<HTMLOutputElement>('#midi-status');
const panicButton = required<HTMLButtonElement>('#panic');
const activeVoices = required<HTMLOutputElement>('#active-voices');
const waveformA = required<HTMLInputElement>('#waveform-a');
const oscillatorShapeA = required<HTMLInputElement>('#oscillator-shape-a');
const shapeLabelA = required<HTMLElement>('#shape-label-a');
const reversePhaseA = required<HTMLInputElement>('#reverse-phase-a');
const invertPolarityA = required<HTMLInputElement>('#invert-polarity-a');
const waveformB = required<HTMLInputElement>('#waveform-b');
const oscillatorShapeB = required<HTMLInputElement>('#oscillator-shape-b');
const shapeLabelB = required<HTMLElement>('#shape-label-b');
const reversePhaseB = required<HTMLInputElement>('#reverse-phase-b');
const invertPolarityB = required<HTMLInputElement>('#invert-polarity-b');
const oscillatorMix = required<HTMLInputElement>('#oscillator-mix');
const attack = required<HTMLInputElement>('#attack');
const decay = required<HTMLInputElement>('#decay');
const sustain = required<HTMLInputElement>('#sustain');
const release = required<HTMLInputElement>('#release');
const performanceSurface = required<HTMLElement>('#performance-surface');
const lfoWaveform = required<HTMLInputElement>('#lfo-waveform');
const lfoRateMode = required<HTMLSelectElement>('#lfo-rate-mode');
const lfoRate = required<HTMLInputElement>('#lfo-rate');
const lfoDivision = required<HTMLSelectElement>('#lfo-division');
const lfoPolarity = required<HTMLSelectElement>('#lfo-polarity');
const lfoResetPhase = required<HTMLInputElement>('#lfo-reset-phase');
const lfoDepth = required<HTMLInputElement>('#lfo-depth');
const filterCutoff = required<HTMLInputElement>('#filter-cutoff');
const filterQ = required<HTMLInputElement>('#filter-q');
const filterMode = required<HTMLSelectElement>('#filter-mode');
const filterEnvelopeDepth = required<HTMLInputElement>('#filter-envelope-depth');
const filterEnvelopeAttack = required<HTMLInputElement>('#filter-envelope-attack');
const filterEnvelopeDecay = required<HTMLInputElement>('#filter-envelope-decay');
const filterEnvelopeSustain = required<HTMLInputElement>('#filter-envelope-sustain');
const filterEnvelopeRelease = required<HTMLInputElement>('#filter-envelope-release');
const moduleLabReadback = required<HTMLOutputElement>('#module-lab-readback');
const referencePlay = required<HTMLButtonElement>('#reference-play');
const referenceDrive = required<HTMLInputElement>('#reference-drive');
const referenceThreshold = required<HTMLInputElement>('#reference-threshold');
const referenceReadback = required<HTMLOutputElement>('#reference-readback');
const processingPlay = required<HTMLButtonElement>('#processing-play');
const processingMorph = required<HTMLInputElement>('#processing-morph');
const processingBits = required<HTMLInputElement>('#processing-bits');
const processingRate = required<HTMLInputElement>('#processing-rate');
const processingPhaser = required<HTMLInputElement>('#processing-phaser');
const processingBypass = required<HTMLInputElement>('#processing-bypass');
const processingReadback = required<HTMLOutputElement>('#processing-readback');
const focusPolicy = new PerformanceFocusPolicy(() => performanceSurface.focus({ preventScroll: true }));

const patchBay = createPatchBay({
  graph,
  workspace: patchWorkspace,
  catalog: moduleCatalog,
  contextMenu: patchContextMenu,
  addModule: required<HTMLButtonElement>('#patch-add-module'),
  moduleStates: new Map(allmostDefaultRuntimeModules.map(({ moduleId, state }) => [moduleId, state])),
  status: patchBayStatus,
  reset: required<HTMLButtonElement>('#patch-reset'),
  removeCable: required<HTMLButtonElement>('#patch-remove-cable'),
  removeNode: required<HTMLButtonElement>('#patch-remove-node'),
  zoomIn: required<HTMLButtonElement>('#patch-zoom-in'),
  zoomOut: required<HTMLButtonElement>('#patch-zoom-out'),
  exportButton: required<HTMLButtonElement>('#patch-export'),
  importButton: required<HTMLButtonElement>('#patch-import'),
  json: required<HTMLTextAreaElement>('#patch-json'),
  parameterPanel: required<HTMLElement>('#patch-parameters'),
  undo: required<HTMLButtonElement>('#patch-undo'),
  redo: required<HTMLButtonElement>('#patch-redo'),
  onChange: () => { refreshGraphOutputs(); if (liveGraph.active) liveGraph.update(patchBay.editor.graph, selectedLiveOutputs()); },
  validateParameter: (moduleId, parameterId, value, nodeId) => {
    const definition = [...allmostGraphModules,...hostDefinitions].find(m => m.descriptor.id === moduleId);
    if (!definition) throw new RangeError('No scalar parameter adapter');
    if (liveDefinitions.includes(definition)) {
      const node = patchBay.editor.graph.nodes.find(n => n.id === nodeId);
      normalizedNode({id:'parameter-probe',moduleId,parameters:{...node?.parameters,[parameterId]:value}},definition,48000);
    } else definition.createInstance({ sampleRate: 48000, maxBlockSize: 128 }).setParameter(parameterId, value);
  },
}, allmostPatch, [...allmostGraphDescriptors,...hostDefinitions.map(d=>d.descriptor)]);
const graphOutput = required<HTMLSelectElement>('#graph-output');
const graphStatus = required<HTMLOutputElement>('#graph-render-status');
const graphOutputRight = required<HTMLSelectElement>('#graph-output-right');
const liveStatus = required<HTMLOutputElement>('#graph-live-status');
const liveButton = required<HTMLButtonElement>('#graph-live');
const liveGraph = createBrowserLiveGraph((state,data) => {
  liveStatus.dataset.state = state;
  liveStatus.textContent = state === 'error' ? `LIVE umlčané · ${String(data?.error)}` : `${state.toUpperCase()} · requested ${data?.requestedRevision ?? liveGraph.requestedRevision} / audible ${data?.appliedRevision ?? liveGraph.appliedRevision}${data?.slots ? ` · slots ${data.slots}` : ''}${data?.rms !== undefined ? ` · RMS ${Number(data.rms).toFixed(4)} · raw peak ${Number(data.rawPeak??0).toFixed(4)} / RMS ${Number(data.rawRms??0).toFixed(4)} · clip ${data.limitedSamples??0} · frames ${data.frames}` : ''}`;
  liveButton.setAttribute('aria-pressed',String(liveGraph.active));
  (globalThis as typeof globalThis & {__ALLMOST_LIVE_GRAPH__?:unknown}).__ALLMOST_LIVE_GRAPH__={state,...data,requestedRevision:liveGraph.requestedRevision,appliedRevision:liveGraph.appliedRevision};
});
const selectedLiveOutputs = () => [JSON.parse(graphOutput.value),...(graphOutputRight.value ? [JSON.parse(graphOutputRight.value)] : [])];
const stopLiveGraph = () => { if (liveGraph.active) liveGraph.stop(); };
let fixedGeneration = 0;
let graphSource: AudioBufferSourceNode | undefined;
const refreshGraphOutputs = (): void => {
  for (const select of [graphOutput,graphOutputRight]) {
    const previous = select.value; select.replaceChildren();
    if (select === graphOutputRight) { const option=document.createElement('option');option.value='';option.textContent='Mono → oba kanály';select.append(option); }
    for (const endpoint of graphAudioOutputs(patchBay.editor.graph)) {
      const option=document.createElement('option');option.value=JSON.stringify(endpoint);option.textContent=`${endpoint.nodeId}.${endpoint.portId}`;select.append(option);
    }
    let preferred=[...select.options].find(o=>o.value===previous);
    if (!preferred && previous && liveGraph.active) { preferred=document.createElement('option');preferred.value=previous;preferred.textContent='Odstránený výstup – vyberte nový';select.append(preferred); }
    preferred ??= [...select.options].find(o=>o.textContent==='mod-025.out');
    if (preferred) select.value=preferred.value;
  }
};
required<HTMLButtonElement>('#graph-outputs-refresh').addEventListener('click', refreshGraphOutputs);
let graphToken = 0;
let cancelGraphRender: (() => void) | undefined;
const graphRenderButton = required<HTMLButtonElement>('#graph-render');
required<HTMLButtonElement>('#graph-stop').addEventListener('click', () => { liveGraph.stop(); graphToken++; cancelGraphRender?.(); graphSource?.stop(); graphSource = undefined; graphStatus.textContent = 'Graf zastavený.'; });
graphRenderButton.addEventListener('click', async () => {
  if (graphRenderButton.disabled) return;
  stopLiveGraph(); instrument.panic();
  graphRenderButton.disabled = true;
  const token = ++graphToken;
  graphStatus.textContent = 'Render grafu v samostatnom workeri…';
  try {
    const audioContext = getContext();
    let resumeTimer: ReturnType<typeof setTimeout> | undefined;
    try { await Promise.race([audioContext.resume(), new Promise<never>((_, reject) => { resumeTimer = setTimeout(() => reject(new Error('Audio je blokované prehliadačom; povoľte zvuk a kliknite znovu.')), 2000); })]); }
    finally { clearTimeout(resumeTimer); }
    if (token !== graphToken) return;
    const snapshot = structuredClone(patchBay.editor.graph);
    const result = await new Promise<ReturnType<typeof renderEditedGraph>>((resolve, reject) => {
      const worker = new Worker(new URL('./graph-worker.js', import.meta.url), { type: 'module' });
      const finish = () => { clearTimeout(timer); worker.terminate(); cancelGraphRender = undefined; };
      const timer = setTimeout(() => { finish(); reject(new Error('Render exceeded 20 seconds')); }, 20000);
      cancelGraphRender = () => { finish(); reject(new Error('Render cancelled')); };
      worker.onmessage = event => { finish(); if (event.data.ok) resolve(event.data.result); else reject(new Error(event.data.error)); };
      worker.onerror = event => { finish(); reject(new Error(event.message)); };
      worker.postMessage({ graph: snapshot, output: JSON.parse(graphOutput.value), sampleRate: audioContext.sampleRate, frames: audioContext.sampleRate * 2 });
    });
    if (token !== graphToken) return;
    const buffer = audioContext.createBuffer(1, result.frames, result.sampleRate); buffer.copyToChannel(new Float32Array(result.samples), 0);
    graphSource?.stop(); source?.stop(); source = undefined;
    startButton.disabled = false; stopButton.disabled = true; status.textContent = 'Fixed v1 stopped · graph audition selected';
    graphSource = audioContext.createBufferSource(); graphSource.buffer = buffer; graphSource.connect(audioContext.destination); graphSource.start();
    graphStatus.textContent = `RENDER ${result.output.nodeId}.${result.output.portId} · peak ${result.peak.toFixed(4)} · RMS ${result.rms.toFixed(4)} · limitované ${result.limitedSamples}`;
    (globalThis as typeof globalThis & { __ALLMOST_GRAPH_RENDER__?: unknown }).__ALLMOST_GRAPH_RENDER__ = { ...result, samples: undefined };
  } catch (error) { if (token === graphToken) graphStatus.textContent = `Graf sa neprehral: ${error instanceof Error ? error.message : String(error)}`; }
  finally { graphRenderButton.disabled = false; }
});
refreshGraphOutputs();
for (const select of [graphOutput,graphOutputRight]) select.addEventListener('change',()=>{if(liveGraph.active)liveGraph.update(patchBay.editor.graph,selectedLiveOutputs());});
required<HTMLButtonElement>('#graph-live-example').addEventListener('click',()=>{
  stopLiveGraph();
  required<HTMLTextAreaElement>('#patch-json').value=JSON.stringify(liveGraphExample());
  required<HTMLButtonElement>('#patch-import').click();
  graphOutput.value=JSON.stringify({nodeId:'phaser',portId:'out'});
  graphOutputRight.value='';
});
liveButton.addEventListener('click',()=>{
  if(liveGraph.active){liveGraph.stop();return;}
  fixedGeneration++;graphToken++;cancelGraphRender?.();graphSource?.stop();graphSource=undefined;source?.stop();source=undefined;instrument.panic();
  void context?.suspend();startButton.disabled=false;stopButton.disabled=true;status.textContent='Fixed v1 stopped · LIVE cable audition selected';
  if(!graphOutput.value){liveStatus.textContent='Vyberte audio výstup';return;}
  void liveGraph.start(patchBay.editor.graph,selectedLiveOutputs());
});

patchOpen.addEventListener('click', () => {
  if (!patchOverlay.open) patchOverlay.showModal();
  patchClose.focus();
});
patchClose.addEventListener('click', () => patchOverlay.close());
patchOverlay.addEventListener('click', (event) => {
  if (event.target === patchOverlay) patchOverlay.close();
});
patchOverlay.addEventListener('close', () => patchOpen.focus({ preventScroll: true }));

const moduleLabOptions = () => ({
  lfoRateHz: lfoRate.valueAsNumber,
  modulationDepthSemitones: lfoDepth.valueAsNumber,
  filterCutoffHz: filterCutoff.valueAsNumber,
  filterQ: filterQ.valueAsNumber,
  filterMode: filterMode.value as ModuleLabFilterMode,
});
const liveFilterSettings = (): LiveFilterSettings => ({
  ...moduleLabOptions(),
  lfoWaveform: lfoWaveform.value as LfoWaveform,
  lfoRateMode: lfoRateMode.value as LfoRateMode,
  lfoTempoBpm: 120,
  lfoDivision: lfoDivision.value as TransportDivision,
  lfoPolarity: lfoPolarity.value as LfoPolarity,
  lfoResetPhase: lfoResetPhase.valueAsNumber,
  envelopeDepthSemitones: filterEnvelopeDepth.valueAsNumber,
  envelopeAttack: filterEnvelopeAttack.valueAsNumber,
  envelopeDecay: filterEnvelopeDecay.valueAsNumber,
  envelopeSustain: filterEnvelopeSustain.valueAsNumber,
  envelopeRelease: filterEnvelopeRelease.valueAsNumber,
});
const labFormats = new Map<HTMLInputElement, (value: number) => string>([
  [lfoRate, (value) => `${value.toFixed(2)} Hz`],
  [lfoResetPhase, (value) => value.toFixed(2)],
  [lfoDepth, (value) => `${value.toFixed(0)} st`],
  [filterCutoff, (value) => `${value.toFixed(0)} Hz`],
  [filterQ, (value) => value.toFixed(2)],
  [filterEnvelopeDepth, (value) => `${value.toFixed(0)} st`],
  [filterEnvelopeAttack, (value) => `${value.toFixed(3)} s`],
  [filterEnvelopeDecay, (value) => `${value.toFixed(3)} s`],
  [filterEnvelopeSustain, (value) => value.toFixed(2)],
  [filterEnvelopeRelease, (value) => `${value.toFixed(3)} s`],
]);
for (const [input, format] of labFormats) {
  const output = required<HTMLOutputElement>(`output[for="${input.id}"]`);
  const refresh = (): void => { output.textContent = format(input.valueAsNumber); };
  input.addEventListener('input', refresh);
  refresh();
}

const smoke = runModuleLabAudio(48_000, 4096, moduleLabOptions());
const referenceSmoke = runReferenceChainAudio(48_000, 4096);
const processingSmoke = runProcessingReferenceAudio(48_000, 4096);
const combSmoke = runCombQaRoute(48_000);
const additiveSmoke = runAdditiveQaRoute(48_000);
const delaySmoke = runDelayReferenceAudio(48_000, 24_000);
const modulationSmoke = runModulationReferenceAudio(48_000, 24_000);
const reverbSmoke = runReverbReferenceAudio(48_000, 48_000);
const mixerSmoke = runMixerQaRoute(48_000);
const spatializerSmoke = runSpatializerQaRoute(48_000);
const resamplerSmoke = runResamplerQaRoute(48_000);
const selfBufferSmoke = runSelfBufferQaRoute(48_000);
const stringSmoke = runStringQaRoute(48_000);
const boreSmoke = runBoreQaRoute(48_000);
const frictionSmoke = runFrictionQaRoute(48_000);
const granularSmoke = runGranularQaRoute(48_000);
const convolutionSmoke = runConvolutionQaRoute(48_000);
const offlineRendererSmoke = runOfflineRendererQaRoute(48_000);
const qa = {
  defaultPatch: {
    id: allmostPatch.id,
    runtimeId: ALLMOST_DEFAULT_RUNTIME.id,
    nodes: allmostPatch.nodes.length,
    cables: allmostPatch.connections.length,
    graphEditsApplyToAudio: ALLMOST_DEFAULT_RUNTIME.graphEditsApplyToAudio,
    activeAudioRoute: ALLMOST_DEFAULT_RUNTIME.activeAudioRoute,
    moduleStates: allmostDefaultRuntimeModules,
  },
  finite: smoke.finite,
  nonZero: smoke.rms > 0.01,
  headroom: smoke.peak <= 0.95,
  peak: smoke.peak,
  rms: smoke.rms,
  reference: {
    finite: referenceSmoke.finite,
    nonZero: referenceSmoke.rms > 0.001,
    headroom: referenceSmoke.peak <= 1,
    peak: referenceSmoke.peak,
    rms: referenceSmoke.rms,
    route: referenceSmoke.audibleRoute,
  },
  comb: { finite: combSmoke.finite, nonZero: combSmoke.nonZero, bounded: combSmoke.bounded, affected: combSmoke.affected, route: combSmoke.route },
  additive: { finite: additiveSmoke.finite, nonZero: additiveSmoke.nonZero, bounded: additiveSmoke.bounded, affected: additiveSmoke.affected, route: additiveSmoke.route },
  delay: { finite: delaySmoke.finite, nonZero: delaySmoke.rms > 0.0001, bounded: delaySmoke.peak <= 1, route: delaySmoke.audibleRoute },
  modulation: { finite: modulationSmoke.finite, nonZero: modulationSmoke.rms > 0.001, bounded: modulationSmoke.peak <= 1, affected: modulationSmoke.differenceRms > 0.0001, route: modulationSmoke.audibleRoute },
  reverb: { finite: reverbSmoke.finite, nonZero: reverbSmoke.rms > 0.001, bounded: reverbSmoke.peak <= 1, affected: reverbSmoke.differenceRms > 0.0001, route: reverbSmoke.audibleRoute },
  mixer: { finite: mixerSmoke.finite, nonZero: mixerSmoke.nonZero, bounded: mixerSmoke.bounded, affected: mixerSmoke.affected, route: mixerSmoke.route },
  string: { finite: stringSmoke.finite, nonZero: stringSmoke.nonZero, bounded: stringSmoke.bounded, stereo: stringSmoke.stereo, route: stringSmoke.audibleRoute },
  bore: { finite: boreSmoke.finite, nonZero: boreSmoke.nonZero, bounded: boreSmoke.bounded, stereo: boreSmoke.stereo, route: boreSmoke.audibleRoute },
  friction: { finite: frictionSmoke.finite, nonZero: frictionSmoke.nonZero, bounded: frictionSmoke.bounded, stereo: frictionSmoke.stereo, route: frictionSmoke.audibleRoute },
  granular: { finite: granularSmoke.finite, nonZero: granularSmoke.nonZero, bounded: granularSmoke.bounded, stereo: granularSmoke.stereo, activeGrains: granularSmoke.activeGrains, route: granularSmoke.audibleRoute },
  convolution: { finite: convolutionSmoke.finite, nonZero: convolutionSmoke.nonZero, bounded: convolutionSmoke.bounded, stereo: convolutionSmoke.stereo, affected: convolutionSmoke.affected, latencyFrames: convolutionSmoke.latencyFrames, provenanceId: convolutionSmoke.provenanceId, sourceHash: convolutionSmoke.sourceHash, route: convolutionSmoke.audibleRoute },
  offlineRenderer: offlineRendererSmoke,
  selfBuffer: { finite: selfBufferSmoke.finite, nonZero: selfBufferSmoke.nonZero, bounded: selfBufferSmoke.bounded, affected: selfBufferSmoke.affected, channelParity: selfBufferSmoke.channelParity, route: selfBufferSmoke.audibleRoute },
  resampler: { finite: resamplerSmoke.finite, nonZero: resamplerSmoke.nonZero, bounded: resamplerSmoke.bounded, affected: resamplerSmoke.affected, route: resamplerSmoke.audibleRoute },
  spatializer: { finite: spatializerSmoke.finite, nonZero: spatializerSmoke.nonZero, bounded: spatializerSmoke.bounded, affected: spatializerSmoke.affected, route: spatializerSmoke.route, sideRms: spatializerSmoke.sideRms },
  processing: {
    finite: processingSmoke.finite,
    nonZero: processingSmoke.rms > 0.0001,
    bounded: processingSmoke.peak <= 1,
    affected: processingSmoke.differenceRms > 0.0001,
    route: processingSmoke.audibleRoute,
  },
};
(globalThis as typeof globalThis & { __ALLMOST_QA__?: typeof qa }).__ALLMOST_QA__ = qa;
status.textContent = `READY · FIXED DEFAULT 36/36 · ${smoke.audibleRoute.join(' → ')} · peak ${smoke.peak.toFixed(3)} · RMS ${smoke.rms.toFixed(3)}`;

let context: AudioContext | undefined;
let source: AudioBufferSourceNode | undefined;
const getContext = (): AudioContext => { stopLiveGraph(); return context ??= new AudioContext(); };

for (const [input, format] of [
  [referenceDrive, (value: number) => `${value.toFixed(2)}×`],
  [referenceThreshold, (value: number) => `${value.toFixed(0).replace('-', '−')} dB`],
] as const) {
  const output = required<HTMLOutputElement>(`output[for="${input.id}"]`);
  const refresh = (): void => { output.textContent = format(input.valueAsNumber); };
  input.addEventListener('input', refresh);
  refresh();
}
const envelope = (): EnvelopeSettings => ({
  attack: attack.valueAsNumber,
  decay: decay.valueAsNumber,
  sustain: sustain.valueAsNumber,
  release: release.valueAsNumber,
});
interface OscillatorControlState {
  pulseWidth: number;
  phaseDistortion: number;
}
const oscillatorAState: OscillatorControlState = { pulseWidth: 0.5, phaseDistortion: 0 };
const oscillatorBState: OscillatorControlState = { pulseWidth: 0.5, phaseDistortion: 0 };
const oscillatorSettings = (
  waveform: HTMLInputElement,
  reversePhase: HTMLInputElement,
  invertPolarity: HTMLInputElement,
  state: OscillatorControlState,
): OscillatorSettings => ({
  waveform: waveform.value as OscillatorSettings['waveform'],
  pulseWidth: state.pulseWidth,
  phaseDistortion: state.phaseDistortion,
  reversePhase: reversePhase.checked,
  invertPolarity: invertPolarity.checked,
});
const oscillators = (): DualOscillatorSettings => ({
  oscillatorA: oscillatorSettings(waveformA, reversePhaseA, invertPolarityA, oscillatorAState),
  oscillatorB: oscillatorSettings(waveformB, reversePhaseB, invertPolarityB, oscillatorBState),
  mix: oscillatorMix.valueAsNumber,
});
const reportVoiceCount = (count: number): void => {
  activeVoices.textContent = `${count} ${count === 1 ? 'hlas' : 'hlasov'}`;
};
type MeteredFilterReadback = LiveFilterReadback & {
  readonly peak: number;
  readonly rms: number;
  readonly cumulativePeak: number;
  readonly cumulativeRms: number;
  readonly cumulativeFrames: number;
  readonly overRangeSamples: number;
};
let latestLiveFilterReadback: MeteredFilterReadback | undefined;
const voiceSink = new WebAudioVoiceSink(
  getContext,
  envelope,
  oscillators,
  liveFilterSettings,
  reportVoiceCount,
  (readback) => {
    latestLiveFilterReadback = readback;
    moduleLabReadback.textContent = `LIVE MOD-001 A+B → MOD-019 → MOD-021 → MOD-034 → MOD-025 · MOD-015 + MOD-014 cutoff · peak ${readback.peak.toFixed(3)} · RMS ${readback.rms.toFixed(3)} · lifetime peak ${readback.cumulativePeak.toFixed(3)} · lifetime RMS ${readback.cumulativeRms.toFixed(3)} · frames ${readback.cumulativeFrames} · ≥1 ${readback.overRangeSamples} · filter env ${readback.envelopeStage} (${readback.heldNotes} held, ${readback.envelopeRetriggers} retriggers) · finite ${String(readback.finite).toUpperCase()}`;
  },
  (error) => { status.textContent = `AUDIO ERROR · ${error.message}`; },
);
const instrument = new LiveInstrumentController(voiceSink);
for (const control of [lfoWaveform, lfoRateMode, lfoRate, lfoDivision, lfoPolarity, lfoResetPhase, lfoDepth, filterCutoff, filterQ, filterMode, filterEnvelopeDepth, filterEnvelopeAttack, filterEnvelopeDecay, filterEnvelopeSustain, filterEnvelopeRelease]) {
  control.addEventListener(control instanceof HTMLSelectElement || control === lfoWaveform ? 'change' : 'input', () => voiceSink.updateFilterSettings());
}

const formatEnvelope = (input: HTMLInputElement): string =>
  input === sustain ? input.valueAsNumber.toFixed(2) : `${input.valueAsNumber.toFixed(3)} s`;
for (const input of [attack, decay, sustain, release]) {
  const output = required<HTMLOutputElement>(`output[for="${input.id}"]`);
  const update = (): void => { output.textContent = formatEnvelope(input); };
  input.addEventListener('input', update);
  update();
}
type ShapeMode = 'pulse-width' | 'phase-distortion';
const bindOscillatorShape = (
  waveform: HTMLInputElement,
  shape: HTMLInputElement,
  label: HTMLElement,
  state: OscillatorControlState,
): void => {
  const output = required<HTMLOutputElement>(`output[for="${shape.id}"]`);
  let mode: ShapeMode = waveform.value === 'pulse' ? 'pulse-width' : 'phase-distortion';
  const store = (): void => {
    if (mode === 'pulse-width') state.pulseWidth = shape.valueAsNumber;
    else state.phaseDistortion = shape.valueAsNumber;
  };
  const configure = (): void => {
    store();
    mode = waveform.value === 'pulse' ? 'pulse-width' : 'phase-distortion';
    const pulse = mode === 'pulse-width';
    label.textContent = pulse ? 'Pulse width' : 'Phase distortion';
    shape.min = pulse ? '0.01' : '-0.95';
    shape.max = pulse ? '0.99' : '0.95';
    shape.step = '0.01';
    shape.value = String(pulse ? state.pulseWidth : state.phaseDistortion);
    output.textContent = shape.valueAsNumber.toFixed(2);
  };
  shape.addEventListener('input', () => {
    store();
    output.textContent = shape.valueAsNumber.toFixed(2);
  });
  waveform.addEventListener('change', configure);
  configure();
};
const bindWaveformPicker = (pickerSelector: string, waveform: HTMLInputElement): void => {
  const picker = required<HTMLElement>(pickerSelector);
  const buttons = [...picker.querySelectorAll<HTMLButtonElement>('.waveform-button')];
  const refresh = (): void => {
    for (const button of buttons) button.setAttribute('aria-pressed', String(button.dataset.waveform === waveform.value));
  };
  for (const button of buttons) {
    button.addEventListener('pointerdown', (event) => {
      focusPolicy.pointerDown(event.pointerId, button, 'click');
    });
    button.addEventListener('click', () => {
      const selected = button.dataset.waveform;
      if (!selected) return;
      waveform.value = selected;
      refresh();
      waveform.dispatchEvent(new Event('change'));
      focusPolicy.nativeClick(button);
    });
  }
  refresh();
};
bindWaveformPicker('#waveform-picker-a', waveformA);
bindWaveformPicker('#waveform-picker-b', waveformB);
bindWaveformPicker('#lfo-waveform-picker', lfoWaveform);
bindOscillatorShape(waveformA, oscillatorShapeA, shapeLabelA, oscillatorAState);
bindOscillatorShape(waveformB, oscillatorShapeB, shapeLabelB, oscillatorBState);
const mixOutput = required<HTMLOutputElement>('output[for="oscillator-mix"]');
const updateMixReadback = (): void => {
  const bPercent = Math.round(oscillatorMix.valueAsNumber * 100);
  mixOutput.textContent = `A ${100 - bPercent}% · B ${bPercent}%`;
};
oscillatorMix.addEventListener('input', updateMixReadback);
updateMixReadback();

const performanceControls = [
  oscillatorShapeA,
  reversePhaseA,
  invertPolarityA,
  oscillatorShapeB,
  reversePhaseB,
  invertPolarityB,
  oscillatorMix,
  attack,
  decay,
  sustain,
  release,
  lfoRateMode,
  lfoRate,
  lfoDivision,
  lfoPolarity,
  lfoResetPhase,
  lfoDepth,
  filterCutoff,
  filterQ,
  filterMode,
  filterEnvelopeDepth,
  filterEnvelopeAttack,
  filterEnvelopeDecay,
  filterEnvelopeSustain,
  filterEnvelopeRelease,
] as const;
for (const control of performanceControls) {
  control.addEventListener('pointerdown', (event) => {
    const pointerEvent = event as PointerEvent;
    const completion = control instanceof HTMLSelectElement
      ? 'change-or-close'
      : control.type === 'checkbox' ? 'click' : 'pointerup';
    focusPolicy.pointerDown(pointerEvent.pointerId, control, completion);
  });
}
for (const checkbox of [reversePhaseA, invertPolarityA, reversePhaseB, invertPolarityB]) {
  const wrapper = checkbox.closest<HTMLElement>('.toggle');
  if (!wrapper) throw new Error(`missing toggle wrapper: #${checkbox.id}`);
  wrapper.addEventListener('pointerdown', (event) => {
    focusPolicy.pointerDown(event.pointerId, checkbox, 'click');
  });
  checkbox.addEventListener('click', () => { focusPolicy.nativeClick(checkbox); });
}
for (const select of [filterMode, lfoRateMode, lfoDivision, lfoPolarity]) {
  const completeSelection = (): void => { focusPolicy.nativeChangeOrClose(select); };
  select.addEventListener('change', completeSelection);
  select.addEventListener('blur', completeSelection);
  select.addEventListener('close', completeSelection);
  select.addEventListener('keyup', (event) => {
    if (event.key === 'Escape' || event.key === 'Enter') queueMicrotask(completeSelection);
  });
}
document.addEventListener('pointerup', (event) => { focusPolicy.pointerUp(event.pointerId); });
document.addEventListener('pointercancel', (event) => { focusPolicy.pointerCancel(event.pointerId); });

const editableTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && (target.matches('input, textarea, select, [contenteditable="true"]') || target.isContentEditable);

document.addEventListener('keydown', (event) => {
  if (instrument.keyDown({
    code: event.code,
    repeat: event.repeat,
    ctrlKey: event.ctrlKey,
    altKey: event.altKey,
    metaKey: event.metaKey,
    editable: focusPolicy.isEditableTarget(event.target, editableTarget(event.target)),
  })) event.preventDefault();
});
document.addEventListener('keyup', (event) => {
  if (instrument.keyUp({ code: event.code, editable: editableTarget(event.target) })) event.preventDefault();
});

interface MidiInputLike {
  readonly id: string;
  readonly name?: string | null;
  onmidimessage: ((event: { readonly data: Uint8Array }) => void) | null;
}
interface MidiAccessLike {
  readonly inputs: { forEach(callback: (input: MidiInputLike) => void): void };
  onstatechange: (() => void) | null;
}
type MidiNavigator = Navigator & { requestMIDIAccess?: () => Promise<MidiAccessLike> };

const attachMidiInputs = (access: MidiAccessLike): void => {
  let count = 0;
  access.inputs.forEach((input) => {
    count += 1;
    input.onmidimessage = ({ data }) => { instrument.midi(data, input.id || input.name || 'midi'); };
  });
  midiStatus.textContent = count === 0 ? 'MIDI pripravené · čakám na zariadenie' : `MIDI aktívne · ${count} vstupov`;
};

midiConnect.addEventListener('click', async () => {
  const requestMIDIAccess = (navigator as MidiNavigator).requestMIDIAccess;
  if (!requestMIDIAccess) {
    midiStatus.textContent = 'Web MIDI nie je v tomto prehliadači dostupné';
    midiConnect.disabled = true;
    return;
  }
  midiConnect.disabled = true;
  midiStatus.textContent = 'Žiadam prístup k MIDI…';
  try {
    const access = await requestMIDIAccess.call(navigator);
    attachMidiInputs(access);
    access.onstatechange = () => attachMidiInputs(access);
    midiConnect.textContent = 'MIDI pripojené';
  } catch (error) {
    midiStatus.textContent = error instanceof Error ? `MIDI: ${error.message}` : 'MIDI prístup zlyhal';
    midiConnect.disabled = false;
  }
});

panicButton.addEventListener('click', () => { instrument.panic(); liveGraph.stop(); });
window.addEventListener('blur', () => instrument.panic());
document.addEventListener('visibilitychange', () => { if (document.hidden) instrument.panic(); });
window.addEventListener('pagehide', () => { instrument.panic(); liveGraph.stop(); });

interface InstrumentDiagnostics {
  noteOn(note?: number, velocity?: number, sourceId?: string, channel?: number): void;
  noteOff(note?: number, sourceId?: string, channel?: number): void;
  panic(): void;
  readonly activeVoices: number;
  readonly soundingVoices: number;
  readonly envelope: EnvelopeSettings;
  readonly oscillators: DualOscillatorSettings;
  readonly filterSettings: LiveFilterSettings;
  readonly filterReadback: MeteredFilterReadback | undefined;
}
const diagnostics: InstrumentDiagnostics = {
  noteOn(note = 60, velocity = 0.8, sourceId = 'qa', channel = 1): void {
    const statusByte = 0x90 | (Math.max(1, Math.min(16, channel)) - 1);
    instrument.midi([statusByte, note, Math.round(Math.max(0, Math.min(1, velocity)) * 127)], sourceId);
  },
  noteOff(note = 60, sourceId = 'qa', channel = 1): void {
    const statusByte = 0x80 | (Math.max(1, Math.min(16, channel)) - 1);
    instrument.midi([statusByte, note, 0], sourceId);
  },
  panic(): void { instrument.panic(); },
  get activeVoices(): number { return instrument.activeVoiceCount; },
  get soundingVoices(): number { return voiceSink.voiceCount; },
  get envelope(): EnvelopeSettings { return envelope(); },
  get oscillators(): DualOscillatorSettings { return oscillators(); },
  get filterSettings(): LiveFilterSettings { return liveFilterSettings(); },
  get filterReadback(): MeteredFilterReadback | undefined { return latestLiveFilterReadback; },
};
(globalThis as typeof globalThis & { __ALLMOST_INSTRUMENT__?: InstrumentDiagnostics }).__ALLMOST_INSTRUMENT__ = diagnostics;

for (const [input, format] of [
  [processingMorph, (value: number) => value.toFixed(2)],
  [processingBits, (value: number) => `${value.toFixed(0)} bit`],
  [processingRate, (value: number) => `${value.toFixed(0)} Hz`],
  [processingPhaser, (value: number) => value.toFixed(2)],
] as const) {
  const output = required<HTMLOutputElement>(`output[for="${input.id}"]`);
  const refresh = (): void => { output.textContent = format(input.valueAsNumber); };
  input.addEventListener('input', refresh);
  refresh();
}

processingPlay.addEventListener('click', async () => {
  const fixedToken = ++fixedGeneration;
  source?.stop();
  const audioContext = getContext();
  processingReadback.textContent = 'RENDERING · deterministic processing route';
  const rendered = runProcessingReferenceAudio(audioContext.sampleRate, audioContext.sampleRate * 2, {
    morph: processingMorph.valueAsNumber,
    bitDepth: processingBits.valueAsNumber,
    rateHz: processingRate.valueAsNumber,
    phaserMix: processingPhaser.valueAsNumber,
    bypass: processingBypass.checked,
  });
  await audioContext.resume();
  if(fixedToken!==fixedGeneration)return;
  const buffer = audioContext.createBuffer(1, rendered.frames, audioContext.sampleRate);
  buffer.copyToChannel(rendered.samples, 0);
  source = audioContext.createBufferSource();
  source.buffer = buffer; source.loop = true; source.connect(audioContext.destination); source.start();
  startButton.disabled = false; stopButton.disabled = false;
  status.textContent = `PLAYING · ${processingBypass.checked ? 'BYPASS' : rendered.audibleRoute.join(' → ')}`;
  processingReadback.textContent = `PLAYING · peak ${rendered.peak.toFixed(3)} · RMS ${rendered.rms.toFixed(3)} · ΔRMS ${rendered.differenceRms.toFixed(3)} · finite ${String(rendered.finite).toUpperCase()}`;
});

referencePlay.addEventListener('click', async () => {
  const fixedToken = ++fixedGeneration;
  source?.stop();
  status.textContent = 'STARTING · accepted DSP reference chain';
  referenceReadback.textContent = 'RENDERING · bounded offline DSP buffer';
  const audioContext = getContext();
  const rendered = runReferenceChainAudio(audioContext.sampleRate, audioContext.sampleRate * 2, {
    drive: referenceDrive.valueAsNumber,
    thresholdDb: referenceThreshold.valueAsNumber,
  });
  referenceReadback.textContent = `RENDERED · peak ${rendered.peak.toFixed(3)} · RMS ${rendered.rms.toFixed(3)} · finite ${String(rendered.finite).toUpperCase()}`;
  await audioContext.resume();
  if(fixedToken!==fixedGeneration)return;
  const buffer = audioContext.createBuffer(1, rendered.frames, audioContext.sampleRate);
  buffer.copyToChannel(rendered.samples, 0);
  source = audioContext.createBufferSource();
  source.buffer = buffer; source.loop = true; source.connect(audioContext.destination); source.start();
  startButton.disabled = false; stopButton.disabled = false;
  status.textContent = `PLAYING · ${rendered.audibleRoute.join(' → ')}`;
  referenceReadback.textContent = `PLAYING · peak ${rendered.peak.toFixed(3)} · RMS ${rendered.rms.toFixed(3)} · finite ${String(rendered.finite).toUpperCase()}`;
});

startButton.addEventListener('click', async () => {
  const fixedToken = ++fixedGeneration;
  graphToken++; cancelGraphRender?.(); graphSource?.stop(); graphSource = undefined;
  graphStatus.textContent = 'Graf zastavený · fixed v1 playback selected.';
  source?.stop();
  const audioContext = getContext();
  await audioContext.resume();
  if(fixedToken!==fixedGeneration)return;
  const rendered = runModuleLabAudio(audioContext.sampleRate, audioContext.sampleRate * 2, moduleLabOptions());
  const buffer = audioContext.createBuffer(1, rendered.frames, audioContext.sampleRate);
  const channel = buffer.getChannelData(0);
  for (let index = 0; index < rendered.frames; index += 1) channel[index] = rendered.samples[index]!;
  source = audioContext.createBufferSource();
  source.buffer = buffer; source.loop = true; source.connect(audioContext.destination); source.start();
  const readyModules = rendered.moduleIds.filter((id) => id !== 'mir.mod-001').join(' + ');
  startButton.disabled = true; stopButton.disabled = false;
  status.textContent = `PLAYING · ${rendered.audibleRoute.join(' → ')} · MOD-015 + MOD-014 cutoff modulation · ${readyModules} ready`;
  moduleLabReadback.textContent = `PLAYING · peak ${rendered.peak.toFixed(3)} · RMS ${rendered.rms.toFixed(3)} · finite ${String(rendered.finite).toUpperCase()}`;
});
stopButton.addEventListener('click', () => {
  fixedGeneration++;stopLiveGraph();
  source?.stop(); source = undefined; startButton.disabled = false; stopButton.disabled = true; status.textContent = 'STOPPED · graph remains loaded';
  moduleLabReadback.textContent = 'STOPPED · settings remain loaded';
});

document.documentElement.dataset.allmost = 'ready';
document.documentElement.dataset.moduleCount = String(allmostDefaultRuntimeModules.length);
document.documentElement.dataset.patchId = allmostPatch.id;
document.documentElement.dataset.runtimeId = ALLMOST_DEFAULT_RUNTIME.id;
document.documentElement.dataset.graphEditsApplyToAudio = String(ALLMOST_DEFAULT_RUNTIME.graphEditsApplyToAudio);
document.documentElement.dataset.audibleRoute = ALLMOST_DEFAULT_RUNTIME.activeAudioRoute.join('>');
document.documentElement.dataset.modulationSource = 'mir.mod-015';
